import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CITY_MAP } from "./cities";
import { SITE } from "./config";
import Seo from "./Seo";
import { Phone, ArrowRight, Truck, Recycle, CheckCircle2 } from "lucide-react";

export default function GravatsPage() {
  const { slug } = useParams();
  const city = CITY_MAP[slug];
  if (!city) return <Navigate to="/" replace />;

  const url = `https://fbenvironnement.fr/gravats/${city.slug}`;
  const title = `Évacuation de gravats à ${city.name} — FB Environnement`;
  const description = `Évacuation et recyclage de gravats à ${city.name} : benne sur chantier ou dépôt direct au centre FB Environnement (${city.distanceKm} km). Tarif au tonnage, bordereau de suivi inclus.`;

  return (
    <div>
      <Seo title={title} description={description} path={`/gravats/${city.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fbenvironnement.fr/" },
            { "@type": "ListItem", position: 2, name: `Gravats ${city.name}`, item: url },
          ],
        })}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm mb-4 text-stone-200">
            <Link to="/" className="hover:text-[#9DCB2E]">Accueil</Link>
            <span className="mx-2 text-stone-400">/</span>
            <span className="text-white font-bold">Gravats {city.name}</span>
          </nav>
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">Spécialité BTP · {city.name}</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">Évacuation de gravats à {city.name}</h1>
          <p className="text-stone-100 text-lg mt-4 max-w-2xl">
            Béton, briques, parpaings, tuiles, carrelage — FB Environnement à Saint-Victoret recycle vos gravats à {city.distanceKm} km de {city.name}. Deux options : on vient avec une benne, ou vous apportez directement.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#9DCB2E] text-[#0f3a18] font-black px-5 py-3 rounded-full shadow-lg shadow-[#9DCB2E]/30"><Phone className="w-4 h-4" strokeWidth={3} /> {SITE.phone}</a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-5 py-3 rounded-full">Devis gravats <ArrowRight className="w-4 h-4" strokeWidth={3} /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-2 gap-5">
        <Link to={`/benne/${city.slug}`} className="bg-white border-2 border-stone-100 hover:border-[#9DCB2E] rounded-3xl p-7 transition">
          <Truck className="w-9 h-9 text-[#1F6B2E] mb-3" />
          <div className="font-heading font-black text-2xl">Benne livrée à {city.name}</div>
          <p className="text-stone-600 mt-2">On dépose une benne sur votre chantier de {city.name} sous 24-48h. Vous remplissez à votre rythme, on récupère pour recyclage. <strong>Idéal pour gros volumes.</strong></p>
          <span className="inline-flex items-center gap-1 mt-3 text-[#1F6B2E] font-bold">Voir les bennes dispos →</span>
        </Link>
        <Link to={`/recyclage/${city.slug}`} className="bg-white border-2 border-stone-100 hover:border-[#9DCB2E] rounded-3xl p-7 transition">
          <Recycle className="w-9 h-9 text-[#1F6B2E] mb-3" />
          <div className="font-heading font-black text-2xl">Dépôt direct au centre</div>
          <p className="text-stone-600 mt-2">Vous chargez votre véhicule et venez à Saint-Victoret ({city.distanceKm} km). Pesée à l&apos;arrivée et au départ. <strong>Plus économique pour petits volumes.</strong></p>
          <span className="inline-flex items-center gap-1 mt-3 text-[#1F6B2E] font-bold">Conditions d&apos;accès →</span>
        </Link>
      </section>

      <section className="bg-stone-50 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading font-black text-3xl">Ce qu&apos;on accepte dans la filière gravats à {city.name}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <div className="bg-white rounded-2xl p-5 border border-stone-100">
              <h3 className="font-heading font-black text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#1F6B2E]" /> Acceptés</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-stone-700">
                <li>• Béton armé ou non, parpaings, briques</li>
                <li>• Tuiles, ardoises, carrelage, faïence</li>
                <li>• Mortier, ciment durci, pierre naturelle</li>
                <li>• Enrobé bitumineux (filière séparée)</li>
                <li>• Plâtre & placo (filière séparée)</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-stone-100">
              <h3 className="font-heading font-black text-lg flex items-center gap-2"><span className="text-rose-500">✕</span> Refusés</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-stone-700">
                <li>• Amiante, fibrociment (filière agréée spécifique)</li>
                <li>• Déchets dangereux (peintures, solvants, batteries)</li>
                <li>• Déchets ménagers en mélange</li>
                <li>• Pneus dans les gravats (filière dédiée)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading font-black text-3xl">Tarif gravats à {city.name}</h2>
        <p className="text-stone-600 mt-2 max-w-2xl">Le prix dépend du volume et du conditionnement. Quelques repères pour votre projet à {city.name} :</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-7">
          <div className="bg-white rounded-3xl p-6 border border-stone-100"><div className="text-xs uppercase tracking-wider font-bold text-stone-500">Au centre</div><div className="font-heading font-black text-3xl mt-1">35 €/t</div><p className="text-sm text-stone-600 mt-2">Prix au tonnage, pesée à l&apos;entrée et sortie. HT.</p></div>
          <div className="bg-white rounded-3xl p-6 border border-stone-100"><div className="text-xs uppercase tracking-wider font-bold text-stone-500">Benne 8 m³</div><div className="font-heading font-black text-3xl mt-1">sur devis</div><p className="text-sm text-stone-600 mt-2">Livraison à {city.name}, location, retrait inclus.</p></div>
          <div className="bg-white rounded-3xl p-6 border border-stone-100"><div className="text-xs uppercase tracking-wider font-bold text-stone-500">Benne 15 m³</div><div className="font-heading font-black text-3xl mt-1">sur devis</div><p className="text-sm text-stone-600 mt-2">Idéale rénovation lourde / petite démolition.</p></div>
        </div>
        <Link to={`/tarifs/${city.slug}`} className="inline-flex items-center gap-2 mt-6 text-[#1F6B2E] font-bold">Voir tous les tarifs à {city.name} <ArrowRight className="w-4 h-4" /></Link>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-gradient-to-br from-[#9DCB2E] to-[#7ba81f] rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0f3a18]">Vos gravats à {city.name}, on s&apos;en occupe.</h2>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#0f3a18] text-white font-bold px-6 py-3 rounded-full"><Phone className="w-4 h-4" /> {SITE.phone}</a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-6 py-3 rounded-full">Demander un devis <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
